import { DashboardService } from '../../../../../service/events/dashboard.service';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, inject, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { BaseForm } from '../../../../base-form';
import { FertilizantesFormDefinition } from './form-control/form-definition';
import { TareasApiService } from '../../../../../service/api/tareas-api.service';
import { FincasApiService } from '../../../../../service/api/fincas-api.service';
import { ProductosApiService } from '../../../../../service/api/productos-api';
import { TrabajadoresApiService } from '../../../../../service/api/trabajadores-api.service';
import { ClientesApiService } from '../../../../../service/api/clientes-api.service';
import { Subject } from 'rxjs';
import { environment } from '../../../../../../environments/environment';
import moment from 'moment';
import { UsuariosApiService } from '../../../../../service/api/usuarios-api.service';

import { TareaFertiModel } from '../../../../../models/tareas/tarea-ferti.model';
import { ParcelasModel } from '../../../../../models/form-common/parcelas.model';
import { FincasModel } from '../../../../../models/form-common/fincas.module';
import { SectorModel } from '../../../../../models/form-common/sector.model';
import { ProductosModel } from '../../../../../models/productos/productos.model';
import { MantenimientoModel } from '../../../../../models/trabajadores/mantenimiento.model';
import { SiexApiService } from '../../../../../service/api/siex-api.service';
import { EventPhase, FormPhase, ReactiveFormRequestCoordinator } from '@fitogest/reactive-form-builder/form-request';
import { BrowserDiagnosticsService } from 'src/app/common/diagnostics/browser-diagnostics.service';
import {
	DropdownData,
	FormType,
	ReactiveFormChangedField,
	ReactiveFormLifecycleEvent,
	ReactiveFormStableValueChange,
	ReactiveFormValue,
} from '@fitogest/reactive-form-builder/interfaces';
import {
    build,
    buildWithParams,
    pool,
    ReactiveCascadeController,
    type ReactiveCascadeResetTarget,
    type ReactiveDropdownCacheConfig,
    ReactiveDropdownCacheService,
    ReactiveDropdownSyncController,
    ReactiveFormPayloadSerializer,
    ReactiveSelectionResolver,
    setDropdownSelected,
    setDropdownSelectedFromArray,
} from '@fitogest/reactive-form-builder/utils';
import { StorageManager } from 'src/app/common/storage-manager.class';

const DROPDOWN_CACHE_TTL_MS = 300000;
const INITIAL_EDIT_PATCH_WAIT_TIMEOUT_MS = 3000;
const INITIAL_EDIT_PATCH_POLL_INTERVAL_MS = 16;
const PREVIOUS_DATA_PATCH_FIELD = '__previous_data_patch';
const DROPDOWN_REFRESH_FIELD = '__dropdown_refresh';

interface InitialPatchWaitResult {
	readonly ready: boolean;
	readonly waitMs: number;
}
@Component({
	selector: 'app-fito-form',
	standalone: false,
	templateUrl: './fertilizantes-form.component.html',
	styleUrls: ['./fertilizantes-form.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FertilizantesFormComponent extends BaseForm<TareaFertiModel> implements OnInit, OnDestroy {
	public loadingData = true;

	public form!: FertilizantesFormDefinition;

	public formRequest = new ReactiveFormRequestCoordinator<TareaFertiModel>();

	public getRequest =
		this.getType() === FormType.DUPLICATION
			? this.tareasApi.fertilizantes.GET_GENERIC
			: this.tareasApi.fertilizantes.GET;

	public recalculateEmmiter = new Subject();

	public mostrarPlan = true;

	public errorMessage = '';

	public planProductos: ProductosModel[] = [];

	public planSectores: SectorModel[] = [];

	public showPlanAbonado = false;

	public isReactiveFormReady = false;

	public reactiveFormLoadingError = '';

	public readonly reactiveFormLoadingLabel = 'Preparando formulario fertilizante...';

	public reactiveFormRecordId = '';

	public initialPreviousData: TareaFertiModel | null = null;

	private skippedInitializationChanges = 0;

	private readonly diagnostics = inject(BrowserDiagnosticsService, { optional: true });

	private readonly npkTriggerFields = new Set<string>([
		'id_finca',
		'ids_sectores',
		'id_parcelas',
		'ids_productos',
		'dosis',
		'gramos',
		'kg_ha',
		'ferti_group',
	]);

	private isInitializingFormData = false;

	private initialFincaDependentDataLoaded = false;

	private deferredSiexCatalogsLoaded = false;

	private initialFormDataCompletionStarted = false;

	private previousDataPatchReceived = false;

	private destroyed = false;


	disabledDates: Date[] = [];

	/*** Firma DonJardin ***/
	public signatureFile: File = new File([], '');

	constructor(
		public fincasApi: FincasApiService,
		public siexApi: SiexApiService,
		public tareasApi: TareasApiService,
		public productosApi: ProductosApiService,
		public trabajadoresApi: TrabajadoresApiService,
		private readonly userApi: UsuariosApiService,
		private readonly dropdownCache: ReactiveDropdownCacheService,
		public override router: Router,
		public override route: ActivatedRoute,
		public dashboardEvents: DashboardService,
		public clientesApi: ClientesApiService,
		private readonly cdr: ChangeDetectorRef,
	) {
		super(
			route,
			router,
			dashboardEvents,
			tareasApi.fertilizantes.PUT,
			tareasApi.fertilizantes.POST,
			'fertilizantes',
			'Editar Tarea Fertilizante',
			'Realizar Tarea Fertilizante',
			'Duplicar Tarea Fertilizante',
		);
		this.formRequest = new ReactiveFormRequestCoordinator<TareaFertiModel>();
	}

	public async ngOnInit(): Promise<void> {
		const scope = this.diagnostics?.start('ng-on-init', 'fertilizantes-form', this.getDiagnosticsMetadata());

		try {
			this.expandFormRequest();
			this.formRequest.executePhase(FormPhase.LOAD);
			this.softInit(this.getType());
			this.resetExternalDropdowns();
			await this.setupFormConFechas();
			this.form.requestType = this.getType();
			this.markReactiveFormReady();

			if (this.form.env.appName === 'detorres') {
				setTimeout(() => {
					this.form.model.fecha_inicio = this.route.snapshot.queryParamMap.get('date')!;
				}, 1500);
			}

			scope?.end(this.getDiagnosticsMetadata());
		} catch (error: unknown) {
			this.loadingData = false;
			this.isInitializingFormData = false;
			this.initialFincaDependentDataLoaded = true;
			this.deferredSiexCatalogsLoaded = true;
			this.finalizeExternalDropdowns();
			this.reactiveFormLoadingError = this.getReadableInitialisationError(error);
			this.cdr.markForCheck();
			scope?.fail(error, this.getDiagnosticsMetadata());
			console.error('No se ha podido preparar el formulario fertilizante.', error);
		}
	}

	public async getEventosAPI(): Promise<{
		dates: Date[];
		eventos: {
			start: string;
			title: string;
			hora_inicio: string;
			hora_fin: string;
		}[];
	}> {
		const fechas: Date[] = [];
		let eventosRaw: any[] = [];

		try {
			eventosRaw = await this.tareasApi.eventos.GET.toPromise();
			for (const ev of eventosRaw) {
				const [dd, mm, yyyy] = ev.start.split('-').map(Number);

				if ([dd, mm, yyyy].every(Number.isInteger)) {
					fechas.push(new Date(yyyy, mm - 1, dd));
				}
			}
		} catch (err) {
			console.error('Error cargando eventos:', err);
		}
		return { dates: fechas, eventos: eventosRaw };
	}

	// SUPPORT

	public stableValueChanges(event: ReactiveFormStableValueChange<TareaFertiModel>): void {
		const changedFieldName = String(event.changedField ?? '');
		const modelChanges = this.captureStableValueChange(event.value as TareaFertiModel);

		this.form.model = this.toFertilizantesModel({
			...this.form.model,
			...modelChanges,
		});
		this.syncModelFromForm();

		if (changedFieldName === PREVIOUS_DATA_PATCH_FIELD) {
			this.previousDataPatchReceived = true;
			this.applyLoadedReactiveState();
			return;
		}

		if (changedFieldName === DROPDOWN_REFRESH_FIELD) {
			this.syncModelFromForm();
			return;
		}

		if (event.origin !== 'user') {
			return;
		}

		this.handleFormValueChange(event.value, event.changedField);
	}

	public reactiveFormChanges(
		formModel: ReactiveFormValue<TareaFertiModel>,
		tag: ReactiveFormChangedField<TareaFertiModel>,
	): void {
		this.stableValueChanges({
			value: formModel,
			changedField: tag,
			changedFields: [String(tag ?? '')],
			origin: 'user',
		});
	}

	public onReactiveFormLifecycleEvent(event: ReactiveFormLifecycleEvent<TareaFertiModel>): void {
		if (event.type !== 'previousDataPatched' && event.type !== 'previousDataChanged') {
			return;
		}

		this.previousDataPatchReceived = true;
		const modelChanges = this.captureStableValueChange(event.value as TareaFertiModel);

		this.form.model = this.toFertilizantesModel({
			...this.form.model,
			...modelChanges,
		});
		this.applyLoadedReactiveState();
	}

	handleFormValueChange(formModel: ReactiveFormValue<TareaFertiModel>, tag: ReactiveFormChangedField<TareaFertiModel>): void {
		const changedFieldName = String(tag ?? '');

		this.form.model = this.toFertilizantesModel(this.withRepeatableLegacyValues(formModel));
		this.form.tag = tag;
		this.form.lastFieldEdited = tag;
		this.syncModelFromForm();

		if (changedFieldName === PREVIOUS_DATA_PATCH_FIELD) {
			this.previousDataPatchReceived = true;
		}

		if (this.isInitializingFormData) {
			this.skippedInitializationChanges += 1;

			if (this.canCompleteInitialFormDataFromCurrentState()) {
				void this.completeInitialFormData(undefined, 0, 0, [], [], { ready: true, waitMs: 0 });
			}

			return;
		}

		const scope = this.diagnostics?.start('form-changes', 'fertilizantes-form', {
			...this.getDiagnosticsMetadata(),
			tag,
		});

		if (changedFieldName === 'id_cliente') {
			this.clearFertilizantesCascade(['id_finca', 'ids_sectores', 'id_parcelas']);
		}

		if (changedFieldName === 'id_finca') {
			this.clearFertilizantesCascade(['ids_sectores', 'id_parcelas']);
		}

		if (changedFieldName === 'ids_sectores') {
			this.clearFertilizantesCascade(['id_parcelas']);
		}

		if (changedFieldName === 'numero_dias' || changedFieldName === 'dias_semana') {
			this.validate();
		}

		const recalculatedNpk = this.shouldRecalculateNpk(tag);

		if (recalculatedNpk) {
			setTimeout(() => {
				this.recalculateEmmiter.next(true);
			}, 5);

			this.recalculateNpkAndRefreshTables('form-changes');
		}

		scope?.end({
			productos: this.form.lists.productos.values.length,
			parcelas: this.form.lists.parcelas.values.length,
			sectores: this.form.lists.sectores.values.length,
			recalculatedNpk,
		});
	}


	public validate() {
		this.errorMessage = '';

		if (this.form.model.dias_semana) {
			const selectedWeekDays = this.resolveSelectedDropdownEntities(
				this.form.lists.weekDays,
				this.getCurrentFormValue('dias_semana'),
			);

			if (selectedWeekDays.length !== parseInt(this.form.model.numero_dias ?? '', 10)) {
				this.errorMessage = 'Días Semana no coincide con Número Días';
			}
		}
	}

	generarInforme() {
		const prod = {} as ProductosModel;

		const prods = this.resolveSelectedDropdownEntities(
			this.form.lists.productos,
			this.getCurrentFormValue('ids_productos'),
		);

		(prods || []).forEach((producto) => {
			if (producto) {
				prod.id = producto.id ?? '';
				prod.nombre = producto.nombre;
				prod.cantidad = +(producto.cantidad ?? 0);
				this.planProductos.push(prod);
			}
		});

		this.resolveSelectedDropdownEntities(
			this.form.lists.sectores,
			this.getCurrentFormValue('ids_sectores'),
		).forEach((sector: SectorModel) => {
			const sect = new SectorModel();

			sect.id = sector.id ?? '';
			sect.nombre = sector.nombre;

			this.planSectores.push(sect);
		});

		this.showPlanAbonado = true;
	}


	private clearFertilizantesCascade(fieldNames: readonly string[]): void {
		const resetTargets: Array<ReactiveCascadeResetTarget<TareaFertiModel>> = [];

		if (fieldNames.includes('id_finca')) {
			resetTargets.push({
				fieldName: 'id_finca',
				value: null,
				resetSelection: (): void => setDropdownSelected(this.form.lists.fincas, null),
			});
		}

		if (fieldNames.includes('ids_sectores')) {
			resetTargets.push({
				fieldName: 'ids_sectores',
				value: this.form.env.showTareasFertiSectorMultiple ? [] : null,
				resetSelection: (): void => setDropdownSelectedFromArray(
					this.form.lists.sectores,
					[],
					this.form.env.showTareasFertiSectorMultiple,
				),
			});
		}

		if (fieldNames.includes('id_parcelas')) {
			resetTargets.push({
				fieldName: 'id_parcelas',
				value: [],
				resetSelection: (): void => setDropdownSelectedFromArray(this.form.lists.parcelas, [], true),
			});
		}

		const { updates } = ReactiveCascadeController.createResetPatch<TareaFertiModel>(resetTargets);

		this.form.model = this.toFertilizantesModel({
			...this.form.model,
			...updates,
		});
		this.formBuilderRef?.updateForm(updates, false);
		this.syncModelFromForm();
		this.cdr.markForCheck();
	}


	private getCurrentFormValue(fieldName: keyof TareaFertiModel): unknown {
		const rawValue = this.formBuilderRef?.model?.getRawValue() as Partial<TareaFertiModel> | undefined;
		const formValue = rawValue?.[fieldName];

		return formValue ?? this.form.model[fieldName];
	}

	private resolveSelectedDropdownEntities<T>(list: DropdownData<T>, rawValue: unknown): T[] {
		return ReactiveSelectionResolver.resolveSelectedValues(list, rawValue);
	}

	private refreshReactiveDropdownMappings(): void {
		ReactiveDropdownSyncController.refreshMappings({ formBuilder: this.formBuilderRef });
	}

	private withRepeatableLegacyValues(formModel: ReactiveFormValue<TareaFertiModel>): ReactiveFormValue<TareaFertiModel> {
		const legacyValues = ReactiveFormPayloadSerializer.serializeRepeatableLegacyFields(
			this.form.formFields,
			formModel as Record<string, unknown>,
		);

		return { ...formModel, ...legacyValues } as ReactiveFormValue<TareaFertiModel>;
	}

	private syncModelFromForm(): void {
		const normalizedModel = this.toFertilizantesModel(this.form.model);

		this.form.model = normalizedModel;
		this.updateHostModel(normalizedModel);
	}

	private toFertilizantesModel(
		sourceModel: ReactiveFormValue<TareaFertiModel> | Partial<TareaFertiModel> | Record<string, unknown>,
	): TareaFertiModel {
		const normalizedModel: Record<string, TareaFertiModel[string]> = {};

		for (const [key, value] of Object.entries(sourceModel)) {
			if (value !== undefined) {
				normalizedModel[key] = value as TareaFertiModel[string];
			}
		}

		return normalizedModel as TareaFertiModel;
	}

	private applyLoadedReactiveState(): void {
		this.syncModelFromForm();
		this.syncFincaDependentSelectedValuesFromModel();
		this.refreshDeclarativeLocationDropdowns();
		this.refreshReactiveDropdownMappings();
		this.recalculateNpkAndRefreshTables('previous-data-hydration');
	}

	public override ngOnDestroy(): void {
		this.destroyed = true;
		this.recalculateEmmiter.complete();
		super.ngOnDestroy();
	}

	formatoHorasMinutos(horaEntrada: string, horaSalida: string) {
		let timeStr: string | string[] = horaEntrada;

		timeStr = timeStr.split(':');

		const h = timeStr[0] ?? '',
			m = timeStr[1] ?? '';

		const newTime = moment(`${horaSalida} 2021-04-05`, 'HH:mm YYYY-MM-DD')
			.subtract({ hours: +h, minutes: +m })
			.format('HH:mm');

		return newTime;
	}

	private initFormData(): Promise<unknown> {
		const scope = this.diagnostics?.start('init-form-data', 'fertilizantes-form', this.getDiagnosticsMetadata());
		let poolCallbackCalls = 0;

		this.isInitializingFormData = true;
		this.skippedInitializationChanges = 0;
		this.initialFincaDependentDataLoaded = false;
		this.deferredSiexCatalogsLoaded = false;
		this.initialFormDataCompletionStarted = false;
		this.previousDataPatchReceived = false;
		this.updateExternalDropdownsReadyState();

		const buildsRequest: Array<ReturnType<typeof build>> = [];
		const cacheHits: string[] = [];
		const cacheMisses: string[] = [];
		const selectedClientId = this.extractEntityId(this.form.model.id_cliente) ?? StorageManager.getClient()?.id ?? null;

		if (this.restoreCachedDropdownList('fincas', this.form.lists.fincas)) {
			cacheHits.push('fincas');
		} else {
			cacheMisses.push('fincas');
			buildsRequest.push(selectedClientId
				? buildWithParams(this.fincasApi.fincasGET, { id_cliente: selectedClientId }, this.form.lists.fincas, 'nombre', true)
				: build(this.fincasApi.fincasGET, this.form.lists.fincas, 'nombre', true));
		}

		if (this.restoreCachedDropdownList('sectores', this.form.lists.sectores)) {
			cacheHits.push('sectores');
		} else {
			cacheMisses.push('sectores');
			buildsRequest.push(selectedClientId
				? buildWithParams(this.fincasApi.sectoresGET, { id_cliente: selectedClientId }, this.form.lists.sectores, 'nombre', true)
				: build(this.fincasApi.sectoresGET, this.form.lists.sectores, 'nombre', true));
		}

		if (this.restoreCachedDropdownList('parcelas', this.form.lists.parcelas)) {
			cacheHits.push('parcelas');
		} else {
			cacheMisses.push('parcelas');
			buildsRequest.push(selectedClientId
				? buildWithParams(this.fincasApi.parcelasGET, { id_cliente: selectedClientId }, this.form.lists.parcelas, 'nombre', true)
				: build(this.fincasApi.parcelasGET, this.form.lists.parcelas, 'nombre', true));
		}

		if (this.restoreCachedDropdownList('trabajadores', this.form.lists.trabajadores)) {
			cacheHits.push('trabajadores');
		} else {
			cacheMisses.push('trabajadores');
			buildsRequest.push(
				build(
					this.trabajadoresApi.trabajadoresGET,
					this.form.lists.trabajadores,
					(it: MantenimientoModel) => it.nombre + ' ' + (it.apellidos || ''),
					true,
				),
			);
		}

		if (this.restoreCachedDropdownList('clientes', this.form.lists.clientes)) {
			cacheHits.push('clientes');
		} else {
			cacheMisses.push('clientes');
			buildsRequest.push(
				build(this.clientesApi.clientesGET, this.form.lists.clientes, {
					or: ['razon_social', 'nombre'],
				}),
			);
		}

		if (this.restoreCachedDropdownList('productos', this.form.lists.productos)) {
			cacheHits.push('productos');
		} else {
			cacheMisses.push('productos');
			buildsRequest.push(build(this.productosApi.productos.GET, this.form.lists.productos, 'nombre', true));
		}

		if (this.restoreCachedDropdownList('maquinarias', this.form.lists.maquinarias)) {
			cacheHits.push('maquinarias');
		} else {
			cacheMisses.push('maquinarias');
			buildsRequest.push(build(this.trabajadoresApi.maquinariaGET, this.form.lists.maquinarias, 'nombre', true));
		}

		if (this.form.env.showAssignUserTasksFitosanitario || this.form.env.showAssignUserInAllTasks) {
			if (this.restoreCachedDropdownList('users', this.form.lists.users)) {
				cacheHits.push('users');
			} else {
				cacheMisses.push('users');
				buildsRequest.push(build(this.userApi.usuariosGET, this.form.lists.users, 'nombre'));
			}
		}

		const baseDataPromise = buildsRequest.length > 0
			? pool(buildsRequest, () => {
					// This callback is called once per resolved request. Keep it lightweight.
					poolCallbackCalls += 1;
				})
			: Promise.resolve(0);

		return baseDataPromise
			.then(() => {
				this.normalizeFincaDependentDropdowns();
				this.persistBaseDropdownCaches();
				this.form.lists.productos.filtered = [...this.form.lists.productos.values];
				this.initialFincaDependentDataLoaded = true;
				this.updateExternalDropdownsReadyState();
				this.refreshReactiveDropdownMappings();
				this.loadingData = false;
				this.cdr.markForCheck();

				// Static SIEX catalogues are useful, but they should not block populated finca-dependent dropdowns.
				void this.loadDeferredSiexCatalogs();

				void this.completeInitialFormDataWhenInitialPatchIsReady(
					scope,
					poolCallbackCalls,
					buildsRequest.length,
					cacheHits,
					cacheMisses,
				);

				return;
			})
			.catch((e: unknown) => {
				this.isInitializingFormData = false;
				this.initialFincaDependentDataLoaded = true;
				this.deferredSiexCatalogsLoaded = true;
				this.updateExternalDropdownsReadyState();
				scope?.fail(e, this.getDiagnosticsMetadata());
				console.error('Error loading fertilizantes dropdowns', e);
				this.loadingData = false;
				this.finalizeExternalDropdowns();
				this.cdr.markForCheck();
			});
	}


	private canCompleteInitialFormDataFromCurrentState(): boolean {
		if (this.initialFormDataCompletionStarted || !this.initialFincaDependentDataLoaded) {
			return false;
		}

		if (!this.getRegisterId()) {
			return true;
		}

		return this.previousDataPatchReceived || this.isInitialEditPatchReady();
	}

	private async completeInitialFormDataWhenInitialPatchIsReady(
		scope: ReturnType<BrowserDiagnosticsService['start']> | undefined,
		poolCallbackCalls: number,
		requestCount: number,
		cacheHits: string[],
		cacheMisses: string[],
	): Promise<void> {
		const initialPatchWait = await this.waitForInitialEditPatch();

		if (!initialPatchWait.ready) {
			scope?.addMetric('initialPatchReady', 0, 'items');
			scope?.addMetric('initialPatchWaitMs', initialPatchWait.waitMs, 'ms');
			return;
		}

		if (this.destroyed) {
			return;
		}

		await this.completeInitialFormData(
			scope,
			poolCallbackCalls,
			requestCount,
			cacheHits,
			cacheMisses,
			initialPatchWait,
		);
	}

	private async completeInitialFormData(
		scope: ReturnType<BrowserDiagnosticsService['start']> | undefined,
		poolCallbackCalls: number,
		requestCount: number,
		cacheHits: string[],
		cacheMisses: string[],
		initialPatchWait: InitialPatchWaitResult,
	): Promise<void> {
		if (this.initialFormDataCompletionStarted) {
			return;
		}

		this.initialFormDataCompletionStarted = true;

		try {
			this.syncFormModelFromReactiveBuilder();
			this.syncFincaDependentSelectedValuesFromModel();
			this.refreshReactiveDropdownMappings();

			if (this.destroyed) {
				return;
			}

			this.recalculateEmmiter.next(true);
			this.recalculateNpkAndRefreshTables('init-form-data-final');
			this.formRequest.executePhase(FormPhase.UPDATE);
			this.initialFincaDependentDataLoaded = true;
			this.updateExternalDropdownsReadyState();
			this.isInitializingFormData = false;
			this.cdr.markForCheck();
			scope?.addMetric('poolCallbackCalls', poolCallbackCalls, 'calls');
			scope?.addMetric('requests', requestCount, 'items');
			scope?.addMetric('cacheHits', cacheHits.length, 'items');
			scope?.addMetric('cacheMisses', cacheMisses.length, 'items');
			scope?.addMetric('initialPatchReady', initialPatchWait.ready ? 1 : 0, 'items');
			scope?.addMetric('initialPatchWaitMs', initialPatchWait.waitMs, 'ms');
			scope?.addMetric('skippedInitializationChanges', this.skippedInitializationChanges, 'calls');
			scope?.addMetric('productos', this.form.lists.productos.values.length, 'items');
			scope?.addMetric('parcelas', this.form.lists.parcelas.values.length, 'items');
			scope?.addMetric('sectores', this.form.lists.sectores.values.length, 'items');
			scope?.addMetric('deferredSiexCatalogs', 1, 'calls');
			scope?.end({
				...this.getDiagnosticsMetadata(),
				cacheHits,
				cacheMisses,
			});
		} catch (error: unknown) {
			this.initialFormDataCompletionStarted = false;
			if (this.destroyed) {
				return;
			}
			this.isInitializingFormData = false;
			this.initialFincaDependentDataLoaded = true;
			this.deferredSiexCatalogsLoaded = true;
			this.updateExternalDropdownsReadyState();
			this.cdr.markForCheck();
			scope?.fail(error, this.getDiagnosticsMetadata());
			console.error('Error completing initial fertilizantes data', error);
		}
	}



	private normalizeFincaDependentDropdowns(): void {
		this.form.lists.sectores.values = this.form.lists.sectores.values.filter((it) => it.label !== '...');
		this.form.lists.sectores.filtered = [...this.form.lists.sectores.values];

		this.form.lists.parcelas.values = this.form.lists.parcelas.values.filter((it) => it.label !== '...');

		if (this.form.env.appName === 'avafitogest') {
			this.form.lists.parcelas.values = this.form.lists.parcelas.values.map((it) => ({
				...it,
				label: this.parcelaLabel(it.value as ParcelasModel),
			}));
		}

		this.form.lists.parcelas.filtered = [...this.form.lists.parcelas.values];
	}

	private async loadDeferredSiexCatalogs(): Promise<void> {
		const scope = this.diagnostics?.start(
			'load-deferred-siex-catalogs',
			'fertilizantes-form',
			this.getDiagnosticsMetadata(),
		);
		let callbackCalls = 0;
		const cacheHits: string[] = [];
		const cacheMisses: string[] = [];
		const catalogRequests: Array<ReturnType<typeof build>> = [];

		if (
			this.restoreCachedDropdownList(
				'siex:buenas-practicas-fertilizantes',
				this.form.lists.buenasPracticasFertilizantes,
			)
		) {
			cacheHits.push('buenasPracticasFertilizantes');
		} else {
			cacheMisses.push('buenasPracticasFertilizantes');
			catalogRequests.push(
				build(
					this.siexApi.buenasPracticasFertilizantesGET,
					this.form.lists.buenasPracticasFertilizantes,
					'buenas_practicas_fertilizantes',
				),
			);
		}

		if (
			this.restoreCachedDropdownList(
				'siex:metodos-aplicacion-fertis',
				this.form.lists.metodosAplicacionFertis,
			)
		) {
			cacheHits.push('metodosAplicacionFertis');
		} else {
			cacheMisses.push('metodosAplicacionFertis');
			catalogRequests.push(
				build(this.siexApi.metodosFertiGET, this.form.lists.metodosAplicacionFertis, 'metodo_de_fertilizacion'),
			);
		}

		if (
			this.restoreCachedDropdownList(
				'siex:tipos-fertilizacion',
				this.form.lists.tiposFertilizacionSIEX,
			)
		) {
			cacheHits.push('tiposFertilizacionSIEX');
		} else {
			cacheMisses.push('tiposFertilizacionSIEX');
			catalogRequests.push(
				build(this.siexApi.tiposFertiGET, this.form.lists.tiposFertilizacionSIEX, 'tipo_de_fertilizacion'),
			);
		}

		try {
			if (catalogRequests.length > 0) {
				await pool(catalogRequests, () => {
					callbackCalls += 1;
				});
			}

			if (this.destroyed) {
				return;
			}

			this.saveDropdownListToCache(
				'siex:buenas-practicas-fertilizantes',
				this.form.lists.buenasPracticasFertilizantes,
			);
			this.saveDropdownListToCache(
				'siex:metodos-aplicacion-fertis',
				this.form.lists.metodosAplicacionFertis,
			);
			this.saveDropdownListToCache(
				'siex:tipos-fertilizacion',
				this.form.lists.tiposFertilizacionSIEX,
			);

			this.deferredSiexCatalogsLoaded = true;
			this.updateExternalDropdownsReadyState();
			this.refreshReactiveDropdownMappings();
			this.formRequest.executePhase(FormPhase.UPDATE);
			this.cdr.markForCheck();
			scope?.addMetric('callbackCalls', callbackCalls, 'calls');
			scope?.addMetric('cacheHits', cacheHits.length, 'items');
			scope?.addMetric('cacheMisses', cacheMisses.length, 'items');
			scope?.addMetric('buenasPracticas', this.form.lists.buenasPracticasFertilizantes.values.length, 'items');
			scope?.addMetric('metodosAplicacion', this.form.lists.metodosAplicacionFertis.values.length, 'items');
			scope?.addMetric('tiposFertilizacion', this.form.lists.tiposFertilizacionSIEX.values.length, 'items');
			scope?.end({
				...this.getDiagnosticsMetadata(),
				cacheHits,
				cacheMisses,
			});
		} catch (error: unknown) {
			if (this.destroyed) {
				return;
			}
			this.deferredSiexCatalogsLoaded = true;
			this.updateExternalDropdownsReadyState();
			this.cdr.markForCheck();
			scope?.fail(error, this.getDiagnosticsMetadata());
			console.error('Error loading deferred SIEX fertilizantes catalogues', error);
		}
	}

	private getDropdownCacheConfig(name: string): ReactiveDropdownCacheConfig {
		return {
			namespace: 'fertilizantes-form',
			name,
			ttlMs: DROPDOWN_CACHE_TTL_MS,
			appName: environment.appName,
			clientScope: this.getDropdownCacheClientId(),
			userScope: StorageManager.getUser()?.id ?? 'no-user',
		};
	}

	private getDropdownCacheClientId(): string {
		return String(this.extractEntityId(this.form?.model?.id_cliente) ?? StorageManager.getClient()?.id ?? 'no-client');
	}

	private restoreCachedDropdownList(name: string, list: unknown): boolean {
		return this.dropdownCache.restore(this.getDropdownCacheConfig(name), list);
	}

	private saveDropdownListToCache(name: string, list: unknown): void {
		this.dropdownCache.save(this.getDropdownCacheConfig(name), list);
	}

	private persistBaseDropdownCaches(): void {
		this.saveDropdownListToCache('fincas', this.form.lists.fincas);
		this.saveDropdownListToCache('sectores', this.form.lists.sectores);
		this.saveDropdownListToCache('parcelas', this.form.lists.parcelas);
		this.saveDropdownListToCache('trabajadores', this.form.lists.trabajadores);
		this.saveDropdownListToCache('clientes', this.form.lists.clientes);
		this.saveDropdownListToCache('productos', this.form.lists.productos);
		this.saveDropdownListToCache('maquinarias', this.form.lists.maquinarias);

		if (this.form.env.showAssignUserTasksFitosanitario || this.form.env.showAssignUserInAllTasks) {
			this.saveDropdownListToCache('users', this.form.lists.users);
		}
	}

	private updateExternalDropdownsReadyState(): void {
		if (this.initialFincaDependentDataLoaded && this.deferredSiexCatalogsLoaded) {
			this.finalizeExternalDropdowns();

			return;
		}

		this.resetExternalDropdowns();
	}

	private async setupFormConFechas(): Promise<void> {
		const scope = this.diagnostics?.start('setup-form-con-fechas', 'fertilizantes-form', this.getDiagnosticsMetadata());
		const rawModel = localStorage.getItem('tratamiento_temp')
			? JSON.parse(localStorage.getItem('tratamiento_temp')!)
			: this.getModel();

		this.initialPreviousData = this.getRegisterId()
			? this.toFertilizantesModel(rawModel)
			: null;

		if (environment.appName === 'detorres') {
			const { dates, eventos } = await this.getEventosAPI();
			this.disabledDates = dates;

			this.form = new FertilizantesFormDefinition(this.getType(), rawModel, null, this.disabledDates, eventos);
		} else {
			this.form = new FertilizantesFormDefinition(this.getType(), rawModel, null, [], []);
		}

		this.form.model = this.initializeHostModel(this.toFertilizantesModel({
			...this.form.model,
			...(rawModel as ReactiveFormValue<TareaFertiModel>),
		}));
		this.reactiveFormRecordId = this.resolveReactiveFormRecordId(this.form.model);
		this.configureDeclarativeLocationDataSources();

		await this.initFormData();
		this.formRequest.executePhase(FormPhase.LOAD);
		scope?.end(this.getDiagnosticsMetadata());
	}


	private configureDeclarativeLocationDataSources(): void {
		this.form.setLocationDataSources({
			fincas: this.fincasApi.fincasGET,
			sectores: this.fincasApi.sectoresGET,
			parcelas: this.fincasApi.parcelasGET,
		});
	}

	private markReactiveFormReady(): void {
		this.isReactiveFormReady = true;
		this.finalizeExternalDropdowns();
		this.refreshReactiveDropdownMappings();
		this.cdr.markForCheck();
	}

	private getReadableInitialisationError(error: unknown): string {
		if (error instanceof Error && error.message.trim() !== '') {
			return 'No se ha podido cargar el formulario fertilizante: ' + error.message;
		}

		return 'No se ha podido cargar el formulario fertilizante. Revisa la conexión e inténtalo de nuevo.';
	}

	private refreshDeclarativeLocationDropdowns(): void {
		this.refreshDeclarativeLocationDropdownsFrom('id_cliente');
	}

	private refreshDeclarativeLocationDropdownsFrom(changedFieldName: string): void {
		if (changedFieldName === 'id_cliente') {
			this.formBuilderRef?.refreshDeclarativeDropdown('id_finca');
			this.formBuilderRef?.refreshDeclarativeDropdown('ids_sectores');
			this.formBuilderRef?.refreshDeclarativeDropdown('id_parcelas');

			return;
		}

		if (changedFieldName === 'id_finca') {
			this.formBuilderRef?.refreshDeclarativeDropdown('ids_sectores');
			this.formBuilderRef?.refreshDeclarativeDropdown('id_parcelas');

			return;
		}

		if (changedFieldName === 'ids_sectores') {
			this.formBuilderRef?.refreshDeclarativeDropdown('id_parcelas');
		}
	}

	private shouldRecalculateNpk(tag: string): boolean {
		return this.npkTriggerFields.has(tag);
	}

	private recalculateNpkAndRefreshTables(source: string): void {
		const scope = this.diagnostics?.start('recalculate-npk-and-refresh-tables', 'fertilizantes-form', {
			...this.getDiagnosticsMetadata(),
			source,
		});

		this.form.npkBuilder.lists = this.form.lists;
		this.syncFincaDependentSelectedValuesFromModel();
		this.form.npkBuilder.model = this.form.model;
		this.form.npkBuilder.calculaNPKs();
		this.form.refreshFertilizantesTablesFromNpk();
		this.publishFormFieldsUpdate();
		scope?.end({
			source,
			productos: this.form.lists.productos.values.length,
			parcelas: this.form.lists.parcelas.values.length,
			sectores: this.form.lists.sectores.values.length,
		});
	}

	private async waitForInitialEditPatch(): Promise<InitialPatchWaitResult> {
		const startedAt = performance.now();

		const routeRegisterId = this.getRegisterId();

		if (!routeRegisterId) {
			return { ready: true, waitMs: 0 };
		}

		while (performance.now() - startedAt < INITIAL_EDIT_PATCH_WAIT_TIMEOUT_MS) {
			if (this.isInitialEditPatchReady()) {
				return {
					ready: true,
					waitMs: Math.round(performance.now() - startedAt),
				};
			}

			await this.delay(INITIAL_EDIT_PATCH_POLL_INTERVAL_MS);
		}

		return {
			ready: this.isInitialEditPatchReady(),
			waitMs: Math.round(performance.now() - startedAt),
		};
	}

	private isInitialEditPatchReady(): boolean {
		this.syncFormModelFromReactiveBuilder();

		const modelId = this.extractEntityId(this.form?.model?.id);
		const routeRegisterId = this.getRegisterId();

		if (modelId === null || routeRegisterId === '' || String(modelId) !== String(routeRegisterId)) {
			return false;
		}

		const fincaId = this.extractEntityId(this.form?.model?.id_finca);
		if (fincaId === null) {
			return false;
		}

		if (!this.form.env.hasClients || !this.form.env.userCanSeeClientes) {
			return true;
		}

		return this.resolveCurrentClientId() !== null;
	}

	private delay(milliseconds: number): Promise<void> {
		return new Promise((resolve) => {
			setTimeout(resolve, milliseconds);
		});
	}

	private syncFormModelFromReactiveBuilder(): void {
		const builderModel = this.formBuilderRef?.model;

		if (!builderModel) {
			return;
		}

		const currentModel = builderModel.getRawValue() as ReactiveFormValue<TareaFertiModel>;

		this.form.model = this.toFertilizantesModel({
			...this.form.model,
			...currentModel,
		});
		this.syncModelFromForm();
	}

	private resolveCurrentClientId(): string | number | null {
		const directClientId = this.extractEntityId(this.form.model.id_cliente);

		if (directClientId !== null) {
			return directClientId;
		}

		const fincaClientId = this.extractClientIdFromFincaValue(this.form.model.id_finca);

		if (fincaClientId !== null) {
			return fincaClientId;
		}

		const fincaId = this.extractEntityId(this.form.model.id_finca);
		const finca = this.findDropdownValueById<FincasModel>(this.form.lists.fincas, fincaId);

		return this.extractClientIdFromFincaValue(finca);
	}

	private extractClientIdFromFincaValue(value: unknown): string | number | null {
		if (typeof value !== 'object' || value === null || !('id_cliente' in value)) {
			return null;
		}

		return this.extractEntityId((value as { id_cliente?: unknown }).id_cliente);
	}

	private syncFincaDependentSelectedValuesFromModel(): void {
		ReactiveDropdownSyncController.syncSelection<TareaFertiModel, FincasModel>({
			formBuilder: this.formBuilderRef,
			fieldName: 'id_finca',
			dropdown: this.form.lists.fincas,
			rawValue: this.form.model.id_finca,
			patchForm: false,
		});
		ReactiveDropdownSyncController.syncSelection<TareaFertiModel, SectorModel>({
			formBuilder: this.formBuilderRef,
			fieldName: 'ids_sectores',
			dropdown: this.form.lists.sectores,
			rawValue: this.form.model.ids_sectores,
			multiple: this.form.env.showTareasFertiSectorMultiple,
			patchForm: false,
		});
		ReactiveDropdownSyncController.syncSelection<TareaFertiModel, ParcelasModel>({
			formBuilder: this.formBuilderRef,
			fieldName: 'id_parcelas',
			dropdown: this.form.lists.parcelas,
			rawValue: this.form.model.id_parcelas,
			multiple: this.form.env.showFertilizantesParcelas,
			patchForm: false,
		});
	}


	private findDropdownValueById<T extends { id?: string | number }>(
		list: unknown,
		id: string | number | null,
	): T | null {
		return ReactiveSelectionResolver.resolveValueById(list as DropdownData<T>, id);
	}


	private publishFormFieldsUpdate(): void {
		if (!this.form?.formFields) {
			return;
		}

		this.form.formFields = {
			...this.form.formFields,
			sections: [...(this.form.formFields.sections ?? [])],
		};

		this.cdr.markForCheck();
	}

	private getDiagnosticsMetadata(): Record<string, unknown> {
		return {
			formType: this.getType(),
			requestType: this.form?.requestType ?? null,
			appName: this.form?.env?.appName ?? environment.appName,
			externalDropdownsReady: this.externalDropdownsReady,
			loadingData: this.loadingData,
			initializingFormData: this.isInitializingFormData,
		};
	}

	private extractEntityId(value: unknown): string | number | null {
		return ReactiveSelectionResolver.extractId(value);
	}

	private parcelaLabel = (p: ParcelasModel) => {
		const nombre = (p.nombre ?? '').toString().trim();
		const variedad = (p.variedad ?? 'SIN VARIEDAD').toString().trim();

		let zonaRiegoAbrev = '';
		if ((p.cultivo ?? '').toLowerCase() === 'arroz') {
			const zr = (p.zona_riego ?? '').toString().toLowerCase();
			if (zr.includes('alta')) zonaRiegoAbrev = 'ZA';
			else if (zr.includes('baja')) zonaRiegoAbrev = 'ZB';
		}

		const variedadConZona = zonaRiegoAbrev ? `${variedad} - ${zonaRiegoAbrev}` : variedad;

		const pol = (p.poligono ?? '').toString().trim();
		const num = (p.numero ?? '').toString().trim();
		const rec = (p.recinto ?? '').toString().trim();
		const codigos = [pol, num, rec].filter(Boolean).join('-');

		const supRaw = (p.superficie_cultivada ?? p.superficie_sigpac ?? p.superficie ?? '0').toString();
		const supNum = parseFloat(supRaw.replace(',', '.')) || 0;
		const supFmt = supNum.toLocaleString('es-ES', {
			minimumFractionDigits: 2,
			maximumFractionDigits: 2,
		});

		const uds = this.form.env.textoSuperficie || 'ha';

		const derecha = [codigos || undefined, `${supFmt} ${uds}`].filter(Boolean).join(' · ');

		return `${nombre} - ${variedadConZona} — ${derecha}`;
	};

	private expandFormRequest(): void {
		this.formRequest.addEvent(FormPhase.LOAD, EventPhase.AFTER, (resolve) => {
			localStorage.removeItem('tratamiento_temp');
			resolve(true);
		});
	}
}
